import { useEffect, useRef, useState, type ComponentType, type FormEvent } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface PromptBarProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Called when the clear/action button is clicked (if provided, renders as a separate button from form submit). */
  onClear?: () => void;
  autoFocus?: boolean;
  /** When this key changes, focus input. */
  focusKey?: string;
  placeholder?: string;
  submitIcon?: ComponentType<{ className?: string }>;
}

export const PromptBar = ({
  value,
  onChange,
  onSubmit,
  onClear,
  autoFocus = false,
  focusKey,
  placeholder = "Ask your Stasht anything...",
  submitIcon: SubmitIcon,
}: PromptBarProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when focusKey changes (e.g., on each navigation to search)
  useEffect(() => {
    if (focusKey && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [focusKey]);

  // Initial autofocus on mount
  useEffect(() => {
    if (autoFocus && !focusKey && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, focusKey]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Dismiss keyboard on touch devices, keep focus on desktop for easy iteration
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    onSubmit();
  };

  const Icon = SubmitIcon ?? Search;

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        className={cn(
          "flex w-full items-center gap-3 rounded-2xl border bg-muted/40 px-4 py-3",
          isFocused ? "border-primary/30 bg-background" : "border-transparent",
        )}
      >
        <Search className={cn("h-4 w-4 text-muted-foreground", isFocused ? "text-primary" : "")} />
        <input
          ref={inputRef}
          value={value}
          onChange={event => onChange(event.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          type="search"
          autoComplete="off"
          name="search"
          className="text-ui-field flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {SubmitIcon && onClear ? (
          // Always mounted so the bar never changes height — invisible until there's text.
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={!value.trim()}
            onClick={onClear}
            aria-label="Clear"
            className={cn(!value.trim() && "invisible")}
          >
            <Icon className="h-4 w-4" />
          </Button>
        ) : SubmitIcon ? (
          <Button type="submit" size="icon" variant="ghost" disabled={!value.trim()} aria-label="Submit prompt">
            <Icon className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </form>
  );
};

export default PromptBar;
